import { emiteEventoDelEmbudo } from 'lib/analitica_del_embudo'

// Eventos del formulario de alta de `/registro` (PRD-134-1, TC-134-1.4.5.3).
//
// Los seis eventos de esta TC miden lo que pasa DENTRO de la página —qué camino se
// elige, qué campo se toca, qué error se ve, cuánto se tarda—, así que van por el
// camino de cliente de TC-134-1.4.5.1: `emiteEventoDelEmbudo` les adjunta las
// propiedades comunes que el servidor publicó en el `<head>` y las empuja al
// `dataLayer`. Aquí no se construye ningún contexto ni se toca `dataLayer`.
//
// La conducta vive en este módulo y no en el controlador Stimulus por lo mismo que
// `registro_controller.js`: el estado que hay que llevar (qué campos se han
// enfocado ya, cuántos veredictos ha dado el nombre, cuándo empezó la página) es
// de la SESIÓN DE PÁGINA, no de un elemento del DOM, y lo comparten dos
// controladores —el de la página y el del formulario—.
//
// **La sesión se abre una sola vez, con `signup_view`.** Mientras no exista, todas
// las funciones de abajo son no-ops que devuelven `false`. Eso acota la
// instrumentación a `/registro` sin ningún condicional: el otro formulario que
// comparte `RegistroController` (`ponencia/pago/_datos_de_acceso`) no abre sesión,
// así que no emite nada.

// Campos del formulario, en el vocabulario del embudo. `terms` no está en la lista
// de la US (email | password | username) y sí se emite: la casilla legal es un
// campo más del formulario y salir de ella sin marcarla es drop-off como el de
// cualquier otro. `form` queda para los errores que no cuelgan de ningún campo.
export const CAMPO_EMAIL = 'email'
export const CAMPO_NOMBRE = 'username'
export const CAMPO_PASSWORD = 'password'
export const CAMPO_TERMINOS = 'terms'

// Con qué se está dando de alta la persona.
export const METODO_EMAIL = 'email'
export const METODO_GOOGLE = 'google'

// Catálogo CERRADO de `error_type`. Espejo del de
// `Analitica::Embudo::ErroresDelRegistro` más `email_domain_unknown`, que solo
// existe en cliente: el aviso de dominio inexistente lo da la comprobación en vivo
// (TC-134-1.1.6.3) y el modelo, en servidor, lo reporta como un `:invalid` más.
export const ERROR_FORMATO = 'invalid_format'
export const ERROR_DOMINIO_DESCONOCIDO = 'email_domain_unknown'
export const ERROR_PASSWORD_DEBIL = 'weak_password'
export const ERROR_NOMBRE_MAL_FORMADO = 'username_invalid_format'
export const ERROR_NOMBRE_EN_USO = 'username_taken'
export const ERROR_TERMINOS = 'terms_not_accepted'

// Los tres veredictos de la comprobación en tiempo real del nombre de Rankiano.
export const RESULTADO_DISPONIBLE = 'available'
export const RESULTADO_EN_USO = 'taken'
export const RESULTADO_MAL_FORMADO = 'invalid_format'

let sesion = null

// Abre la sesión de página y emite `signup_view`. Todo lo que mide esta TC se
// cuenta desde aquí, incluido el cronómetro de `duration_ms`: si la persona
// recarga, la sesión se abre de nuevo y el cronómetro vuelve a cero.
//
// Los errores que el servidor acaba de rechazar llegan ya resueltos a su par
// `field` / `error_type` (`Analitica::Embudo::ErroresDelRegistro`) y se emiten a
// continuación, de modo que `signup_field_error` sale siempre por este mismo
// camino, lo pinte el navegador o lo haya rechazado el servidor.
export function iniciaSesionDeRegistro ({ source, triggerAction, errores = [] } = {}) {
  sesion = {
    inicio: Date.now(),
    camposEnfocados: new Set(),
    metodosElegidos: new Set(),
    // Último error emitido por campo. Un error se emite al aparecer, no en cada
    // repintado: la interfaz reevalúa en cada pulsación y sin esto un solo error
    // saldría decenas de veces.
    erroresVigentes: new Map(),
    ultimoVeredictoDelNombre: null,
    veredictosDelNombre: 0,
    avisoDeDominio: false,
    dominioCorregido: false
  }

  emiteEventoDelEmbudo('signup_view', { source, trigger_action: triggerAction })
  errores.forEach((error) => emiteErrorDeCampo(error.field, error.error_type))

  return sesion
}

export function cierraSesionDeRegistro () {
  sesion = null
}

export function sesionDeRegistro () {
  return sesion
}

// Una vez por método: elegir camino es una decisión, no una interacción repetida.
export function emiteMetodoElegido (method) {
  if (!sesion || !method || sesion.metodosElegidos.has(method)) return false

  sesion.metodosElegidos.add(method)

  return emiteEventoDelEmbudo('signup_method_clicked', { method })
}

// Solo el PRIMER foco de cada campo en la sesión de página, que es lo que pide la
// US: mide qué campos llega a tocar la persona, no cuántas veces vuelve a ellos.
export function emiteCampoEnfocado (field) {
  if (!sesion || !field || sesion.camposEnfocados.has(field)) return false

  sesion.camposEnfocados.add(field)

  return emiteEventoDelEmbudo('signup_field_focused', { field })
}

export function emiteErrorDeCampo (field, errorType) {
  if (!sesion || !field || !errorType) return false
  if (sesion.erroresVigentes.get(field) === errorType) return false

  sesion.erroresVigentes.set(field, errorType)

  return emiteEventoDelEmbudo('signup_field_error', { field, error_type: errorType })
}

// El campo ha dejado de tener error. No emite nada: solo levanta la marca, para
// que si el mismo error vuelve a aparecer se cuente otra vez.
export function limpiaErrorDeCampo (field) {
  if (sesion) sesion.erroresVigentes.delete(field)
}

// `deServidor` distingue los dos orígenes del veredicto y no es un detalle:
//
//   - el del SERVIDOR (`available` / `taken`) llega cuando una comprobación
//     termina de verdad, así que se emite siempre, aunque repita el anterior;
//   - el de FORMATO lo resuelve el navegador en cada pulsación, así que se emite
//     solo cuando el veredicto cambia. Sin ese filtro, teclear un nombre corto
//     emitiría un `invalid_format` por letra.
export function emiteResultadoDelNombre (result, { deServidor = false } = {}) {
  if (!sesion || !result) return false
  if (!deServidor && sesion.ultimoVeredictoDelNombre === result) return false

  sesion.ultimoVeredictoDelNombre = result
  sesion.veredictosDelNombre += 1

  return emiteEventoDelEmbudo('signup_username_validation_result', { result })
}

// El formulario ha avisado de que el dominio del correo no existe. Es lo más
// parecido a una "sugerencia de typo" que hay en producción: TC-134-1.1.6.3 se
// entregó avisando y SIN proponer corrección (decisión de Producto). Ver el
// catálogo de eventos para la equivalencia con los nombres de la US.
export function marcaAvisoDeDominio () {
  if (sesion) sesion.avisoDeDominio = true
}

// El dominio del correo ha pasado la comprobación. Solo cuenta como corrección si
// antes hubo aviso: si nunca lo hubo, no hay nada que corregir.
export function marcaDominioComprobado () {
  if (sesion && sesion.avisoDeDominio) sesion.dominioCorregido = true
}

export function emiteEnvioDelFormulario ({ method = METODO_EMAIL } = {}) {
  if (!sesion) return false

  return emiteEventoDelEmbudo('signup_form_submitted', {
    method,
    // Desde `signup_view`, en la MISMA sesión de página. Una recarga la reinicia.
    duration_ms: Date.now() - sesion.inicio,
    had_domain_typo_warning: sesion.avisoDeDominio,
    fixed_domain_after_warning: sesion.dominioCorregido,
    nick_attempts: sesion.veredictosDelNombre
  })
}
